import { FC, useState, useCallback, useEffect, useRef, MouseEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import ReactToPrint from 'react-to-print'
import styles from 'styles/components/template.module.scss'
import PageHedding from 'components/PageHedding/PageHedding'
import defaultBread from 'images/default-bread.png'
import SubmitButton from 'components/Button/SubmitButton'
import RadioButton from 'components/Form/RadioButton'
import BreadDetailBoxPrimaryV2 from 'components/Table/BreadDetailBoxPrimaryV2'
import AccordionField from 'components/Accordion/Field'
import NumberBox from 'components/Box/NumberBox'
import LossRateBox from 'components/Box/LossRateBox'
import AllergenIconBox, {
  Allergens as AllergensIcon,
} from 'components/Box/AllergenIconBox'
import { Allergens } from 'reducks/bread/slice'
import { CsvOutputMaterialCostInfo } from 'components/Templates/MaterialTemplate'
import {
  getBreadById,
  clearBread,
  BreadType,
  BackingStickerStatus,
  BreadStatus,
} from 'reducks/bread/slice'
import {
  getRecipeByBreadId,
  RecipeType,
  clearRecipe,
} from 'reducks/recipe/slice'
import {
  getMaterialInfomationByBreadId,
  BreadDetailInformationType,
  BreadDetailPetternInformationType,
} from 'reducks/business/slice'
import { MaterialType, getMaterialById } from 'reducks/material/slice'
import {
  getMaterialCostByMaterialId,
  MaterialCostType,
  SupplierType,
} from 'reducks/materialCost/slice'
import { AdditiveUseType } from 'reducks/additiveUse/slice'
import { getAdditiveUseById } from 'reducks/additiveUse/slice'
import { setIsLoading } from 'reducks/loading/slice'
import { RootState } from 'reducks/reducers'
import { TAX } from 'Constants'
import ExcelJS from 'exceljs'
import { ratioToPercent } from 'utils/number'
import { resolveCostDisplayState } from 'utils/costDisplay'

// レシピ形式ダウンロード用の行。既存の原材料行に、属する生地・フィリングの
// ゾーン（生地/フィリング）とパターン名の階層（最上位→最下位）を付与する
type RecipeCsvOutputMaterialCostInfo = CsvOutputMaterialCostInfo & {
  zone: string
  patternNames: string[]
}

// 生地・フィリング（レシピ階層）ごとに、配下の原材料IDを順序付きで列挙したグループ
type RecipeMaterialGroup = {
  // ゾーン（生地 / フィリング / レシピ）
  zone: string
  // パターン名の階層（最上位パターン→入れ子の子パターンの順）。
  // 例: きのこソース配下のきのこ具材 → ['きのこソース', 'きのこ具材']
  // 生地/フィリング直下に直接置かれた原材料は空配列
  patternPath: string[]
  materialIds: string[]
}

// petternInfo の階層を辿り、生地→フィリング→（あれば）サブレシピの順に
// 「グループ名 → 配下の原材料ID」のリストを作る。原材料の重複排除はしない（レシピ通りに出す）。
//
// 生地/フィリング/レシピの配列には、パターン（生地パターン等）だけでなく、
// 原材料を直接指定したノード（例: フィリングにチーズを直接追加）も並ぶ。
// 前者は productName をグループ名にして配下材料を出力し、後者は
// セクション名（生地/フィリング）をグループ名にしてまとめて出力する。
const collectRecipeGroups = (
  info: BreadDetailInformationType,
): RecipeMaterialGroup[] => {
  const groups: RecipeMaterialGroup[] = []

  // ノード直下のleaf原材料IDのみを取り出す（入れ子の生地/フィリングは含めない）
  const directMaterialIds = (
    node: BreadDetailPetternInformationType,
  ): string[] =>
    (node.materials ?? [])
      .filter((material) => material.materialId)
      .map((material) => material.materialId as string)

  // パターン or サブレシピのノード。直下材料を「ゾーン＋パターン階層」でグループ化し、入れ子を辿る。
  // parentPath は祖先パターン名（最上位→最下位）。自ノードのパターン名を末尾に追加して伝播する。
  const walkContainer = (
    node: BreadDetailPetternInformationType,
    zoneLabel: string,
    parentPath: string[],
  ) => {
    const currentPath = [...parentPath, node.productName]
    const ids = directMaterialIds(node)
    if (ids.length > 0) {
      groups.push({
        zone: zoneLabel,
        patternPath: currentPath,
        materialIds: ids,
      })
    }
    walkSection(node.doughs, '生地', currentPath)
    walkSection(node.fillings, 'フィリング', currentPath)
    walkSection(node.recipes, zoneLabel, currentPath)
  }

  // 生地/フィリング/レシピのセクション配列を処理する。
  // 原材料を直接指定したノード（materialId あり）は親パターンのパスでまとめ、
  // パターン/レシピノード（materialId なし）は walkContainer で展開する。
  const walkSection = (
    nodes: BreadDetailPetternInformationType[] | null,
    zoneLabel: string,
    parentPath: string[],
  ) => {
    if (!nodes) {
      return
    }
    const bareMaterialIds: string[] = []
    nodes.forEach((node) => {
      if (node.materialId) {
        bareMaterialIds.push(node.materialId)
      } else {
        walkContainer(node, zoneLabel, parentPath)
      }
    })
    if (bareMaterialIds.length > 0) {
      groups.push({
        zone: zoneLabel,
        patternPath: parentPath,
        materialIds: bareMaterialIds,
      })
    }
  }

  walkSection(info.doughs, '生地', [])
  walkSection(info.fillings, 'フィリング', [])
  walkSection(info.recipes, 'レシピ', [])

  return groups
}

const BreadDetailTemplate: FC = () => {
  const dispatch = useDispatch<any>()
  const navigate = useNavigate()
  const location = useLocation()
  const componentRef = useRef<HTMLDivElement>(null)
  // 更新の場合か確認
  const path = location.pathname
  const breadId = path.split('/bread/detail/')[1]
    ? path.split('/bread/detail/')[1]
    : null
  const [breadName, setBreadName] = useState<string>('')
  const [breadThumbnail, setBreadThumbnail] = useState<string>('')
  const [breadBackingStickerStatus, setBreadBackingStickerStatus] =
    useState<BackingStickerStatus>(BackingStickerStatus.NG)
  // 裏貼りシールが解析中の要因になっている未登録原材料名
  const [sealAnalysisMaterialNames, setSealAnalysisMaterialNames] = useState<
    string[]
  >([])
  const [breadStatus, setBreadStatus] = useState<BreadStatus>(BreadStatus.DRAFT)
  const [breadRecipeStatus, setBreadRecipeStatus] = useState<BreadStatus>(
    BreadStatus.DRAFT,
  )
  const [energy, setEnergy] = useState<string>('')
  const [protein, setProtein] = useState<string>('')
  const [lipid, setLipid] = useState<string>('')
  const [carbohydrate, setCarbohydrate] = useState<string>('')
  const [saltEquivalent, setSaltEquivalent] = useState<string>('')
  const [materialNames, setMaterialNames] = useState<string>('')
  const [additives, setAdditives] = useState<string>('')
  const [allergens, setAllergens] = useState<string[]>([])
  const [petternInfo, setPetternInfo] = useState<BreadDetailInformationType>({
    recipes: null,
    doughs: null,
    fillings: null,
    allMaterials: [],
    backSealInformation: {
      energy: '',
      protein: '',
      lipid: '',
      carbohydrate: '',
      saltEquivalent: '',
      materialNames: '',
      materialNamesIncludeComposeMaterial: '',
      additives: '',
      baseAllergens: [],
      additiveAllergens: [],
    },
    costPrice: null,
    materialCost: null,
    notCostCalculatedReasons: {},
    notCostCalculatedRecipeReasons: [],
    hasUnregisteredMaterial: false,
    unregisteredMaterialNames: [],
  })
  const [memo, setMemo] = useState<string>('')
  const [process, setProcess] = useState<string>('')
  const [sellPrice, setSellPrice] = useState<string | null>(null)
  const [costPrice, setCostPrice] = useState<string | null>(null)
  const [packagingCostPrice, setPackagingCostPrice] = useState<string | null>(
    null,
  )
  const [isCostCalculating, setIsCostCalculating] = useState<boolean>(false)
  // ライブ原価計算で未登録原材料・解決できない参照を検出したか（原価が出せない理由の判定に使う）
  const [hasUnregisteredMaterial, setHasUnregisteredMaterial] =
    useState<boolean>(false)
  const [unregisteredMaterialNames, setUnregisteredMaterialNames] = useState<
    string[]
  >([])
  const [amountDoughBeforeBaking, setAmountDoughBeforeBaking] = useState<
    string | null
  >(null)
  const [lossRate, setLossRate] = useState<number>(0)
  const [isDisplayUnitPricePerG, setIsDisplayUnitPricePerG] =
    useState<boolean>(true)
  const [isDisplayUnitPricePerKG, setIsDisplayUnitPricePerKG] =
    useState<boolean>(false)
  const [sourceBreadId, setSourceBreadId] = useState<string>('')
  const [materialCsvData, setMaterialCsvData] = useState<
    CsvOutputMaterialCostInfo[]
  >([])
  const [recipeCsvData, setRecipeCsvData] = useState<
    RecipeCsvOutputMaterialCostInfo[]
  >([])

  const handleDisplayUnitPrice = () => {
    setIsDisplayUnitPricePerG(!isDisplayUnitPricePerG ? true : false)
    setIsDisplayUnitPricePerKG(!isDisplayUnitPricePerKG ? true : false)
  }

  const { staff } = useSelector((state: RootState) => state.staffSlice)
  const initBreadInfo = useCallback(
    async (breadId: string | null) => {
      if (!staff) {
        navigate('/initload?path=' + path)
        window.scrollTo(0, 0)
        return
      }
      dispatch(setIsLoading({ isLoading: true }))
      await dispatch(clearBread())
      await dispatch(clearRecipe())
      if (!breadId) {
        // 商品IDが取得できない場合はTOPに飛ばす
        navigate('/initload')
        window.scrollTo(0, 0)
        return
      }
      setSourceBreadId(breadId)
      const breadPayload = await dispatch(getBreadById({ id: breadId }))
      const bread: BreadType = breadPayload.payload
      if (!bread) {
        // 商品が取得できない場合はTOPに飛ばす
        navigate('/initload')
        window.scrollTo(0, 0)
        return
      }
      setBreadName(bread.name)
      setBreadThumbnail(bread.image_url)
      setBreadBackingStickerStatus(bread.backing_sticker_status)
      setSealAnalysisMaterialNames(
        bread.back_seal_analysis_material_names ?? [],
      )
      setBreadStatus(bread.daseruno_status)
      setBreadRecipeStatus(bread.daseruno_recipe_status)
      setSellPrice(bread.selling_price ? String(bread.selling_price) : null)
      setPackagingCostPrice(
        bread.packaging_cost ? String(bread.packaging_cost) : null,
      )
      setIsCostCalculating(
        bread.is_cost_calculating ? bread.is_cost_calculating : false,
      )
      const infoPayload = await dispatch(
        getMaterialInfomationByBreadId({ breadId: breadId }),
      )
      const info: BreadDetailInformationType | null = infoPayload.payload
      setHasUnregisteredMaterial(info ? info.hasUnregisteredMaterial : false)
      setUnregisteredMaterialNames(info ? info.unregisteredMaterialNames : [])
      // レシピが下書き(複製直後を含む)のうちは原価を確定させない。
      // レシピ登録画面で保存して初めて確定原価を表示する。
      const isRecipeUnconfirmed =
        bread.daseruno_recipe_status !== BreadStatus.PUBLIC
      // 原価は保存済みの bread.cost_price ではなくライブ再計算値を表示する。
      // (保存値は材料費変更後に再計算されず古いままのことがあり、同画面のライブ材料費とズレるため)
      // info.costPrice は Math.floor(finalCost) 済み。算出不可の場合は null になる。
      setCostPrice(
        !isRecipeUnconfirmed && info && info.costPrice != null
          ? String(info.costPrice)
          : null,
      )
      if (info) {
        setPetternInfo(info)
        setEnergy(info.backSealInformation.energy)
        setProtein(info.backSealInformation.protein)
        setLipid(info.backSealInformation.lipid)
        setCarbohydrate(info.backSealInformation.carbohydrate)
        setSaltEquivalent(info.backSealInformation.saltEquivalent)
        setMaterialNames(
          info.backSealInformation.materialNamesIncludeComposeMaterial,
        )
        setAdditives(info.backSealInformation.additives)
        // 原材料と添加物のアレルゲンは一緒に表示する
        const allergens: string[] = Array.from(
          new Set([
            ...info.backSealInformation.baseAllergens,
            ...info.backSealInformation.additiveAllergens,
          ]),
        )
        setAllergens(allergens)
      }
      const recipePayload = await dispatch(getRecipeByBreadId({ id: breadId }))
      const recipe: RecipeType = recipePayload.payload
      if (recipe) {
        setMemo(recipe.memo)
        setProcess(recipe.process)
        setAmountDoughBeforeBaking(
          recipe.amount_dough_before_baking
            ? String(recipe.amount_dough_before_baking)
            : null,
        )
        setLossRate(recipe.loss_rate)
      }
      dispatch(setIsLoading({ isLoading: false }))
    },
    [breadId],
  )

  useEffect(() => {
    initBreadInfo(breadId)
  }, [])

  const { materialCategories } = useSelector(
    (state: RootState) => state.materialCategorySlice,
  )
  const { materialSubCategories } = useSelector(
    (state: RootState) => state.materialSubCategorySlice,
  )

  const buildMaterialCostRow = useCallback(
    async (materialId: string): Promise<CsvOutputMaterialCostInfo | null> => {
      if (!materialId) {
        return null
      }
      const materialPayload = await dispatch(
        getMaterialById({ id: materialId }),
      )
      const material: MaterialType = materialPayload.payload
      const materialCostPayload = await dispatch(
        getMaterialCostByMaterialId({ id: materialId }),
      )
      const materialCost: MaterialCostType = materialCostPayload.payload
      const usedCategories = materialCategories.filter((materialCategory) => {
        if (materialCategory.id === material.material_category_ref.id) {
          return true
        }
        return false
      })
      let categoryName = ''
      if (usedCategories.length > 0) {
        categoryName = usedCategories[0].name
      }
      const usedSubCategories = materialSubCategories.filter(
        (materialSubCategory) => {
          if (
            materialSubCategory.id === material.material_sub_category_ref.id
          ) {
            return true
          }
          return false
        },
      )
      let subCategoryName = ''
      if (usedSubCategories.length > 0) {
        subCategoryName = usedSubCategories[0].name
      }
      let supplier: SupplierType | null = null
      let unitAdjustment = 1
      let gramPerPieceAdjustment = 1
      if (materialCost) {
        const usedSuppliers = materialCost.suppliers.filter((supplier) => {
          if (supplier.used_costing) {
            return true
          }
          return false
        })
        if (usedSuppliers.length > 0) {
          supplier = usedSuppliers[0]
          if (
            (supplier.internal_capacity_unit &&
              supplier.internal_capacity_unit === 'kg') ||
            (supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_unit &&
              supplier.internal_capacity.internal_capacity_unit === 'kg')
          ) {
            unitAdjustment = 1000
          } else if (
            (supplier.internal_capacity_unit &&
              supplier.internal_capacity_unit === 'mL') ||
            (supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_unit &&
              supplier.internal_capacity.internal_capacity_unit === 'mL')
          ) {
            unitAdjustment = 1
          } else if (
            (supplier.internal_capacity_unit &&
              supplier.internal_capacity_unit === 'L') ||
            (supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_unit &&
              supplier.internal_capacity.internal_capacity_unit === 'L')
          ) {
            unitAdjustment = 1000
          }
          if (
            supplier.gram_per_piece_unit &&
            supplier.gram_per_piece_unit === 'kg'
          ) {
            gramPerPieceAdjustment = 1000
          }
        }
      }
      const row: CsvOutputMaterialCostInfo = {
        materialId: materialId,
        productName: material ? material.product_name : '',
        manufacturer: material ? material.manufacturer : '',
        categoryName: categoryName,
        subCategoryName: subCategoryName,
        internalCapacityCount:
          supplier && supplier.internal_capacity_count
            ? String(supplier.internal_capacity_count)
            : supplier &&
              supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_count
            ? String(supplier.internal_capacity.internal_capacity_count)
            : '',
        internalCapacityUnit:
          supplier && supplier.internal_capacity_unit
            ? supplier.internal_capacity_unit
            : supplier &&
              supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_unit
            ? supplier.internal_capacity.internal_capacity_unit
            : '',
        numberItem:
          supplier && supplier.number_item
            ? String(supplier.number_item)
            : supplier &&
              supplier.internal_capacity &&
              supplier.internal_capacity.number_item
            ? String(supplier.internal_capacity.number_item)
            : '',
        numberItemUnit:
          supplier && supplier.number_item_unit
            ? supplier.number_item_unit
            : supplier &&
              supplier.internal_capacity &&
              supplier.internal_capacity.number_item_unit
            ? supplier.internal_capacity.number_item_unit
            : '',
        supplierCategory:
          supplier &&
          supplier.supplier_category &&
          supplier.supplier_category === 'wholesale'
            ? '問屋'
            : supplier &&
              supplier.supplier_category &&
              supplier.supplier_category === 'direct'
            ? '直販'
            : supplier &&
              supplier.supplier_category &&
              supplier.supplier_category === 'other'
            ? 'その他'
            : '',
        supplierCompanyName: supplier ? supplier.supplier_company_name : '',
        supplierBranchName: supplier ? supplier.supplier_branch_name : '',
        wholesalerCode:
          supplier && supplier.wholesaler_ref ? supplier.wholesaler_ref.id : '',
        productCode:
          supplier && supplier.product_code ? supplier.product_code : '',
        costPrice: supplier ? String(supplier.cost_price) : '',
        costPriceIncludeTax: supplier
          ? String((supplier.cost_price * (1 + supplier.tax / 100)).toFixed(2))
          : '',
        tax: supplier ? String(supplier.tax) : '',
        ediblePart:
          materialCost && materialCost.edible_part
            ? String(materialCost.edible_part)
            : '',
        inEdiblePart:
          materialCost && materialCost.in_edible_part
            ? String(materialCost.in_edible_part)
            : '',
        yieldRate:
          materialCost && materialCost.yield_rate
            ? String(materialCost.yield_rate)
            : '',
        gramUnitPrice:
          supplier &&
          supplier.internal_capacity_count &&
          supplier.number_item &&
          materialCost
            ? String(
                (
                  (Number(supplier.cost_price) * (supplier.tax / 100 + 1)) /
                  (materialCost.yield_rate / 100) /
                  Number(supplier.internal_capacity_count) /
                  Number(supplier.number_item) /
                  unitAdjustment
                ).toFixed(2),
              )
            : supplier &&
              supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_count &&
              supplier.internal_capacity.number_item &&
              materialCost
            ? String(
                (
                  (Number(supplier.cost_price) * (supplier.tax / 100 + 1)) /
                  (materialCost.yield_rate / 100) /
                  Number(supplier.internal_capacity.internal_capacity_count) /
                  Number(supplier.internal_capacity.number_item) /
                  unitAdjustment
                ).toFixed(2),
              )
            : '',
        kilogramUnitPrice:
          supplier &&
          supplier.internal_capacity_count &&
          supplier.number_item &&
          materialCost
            ? String(
                (
                  ((Number(supplier.cost_price) * (supplier.tax / 100 + 1)) /
                    (materialCost.yield_rate / 100) /
                    Number(supplier.internal_capacity_count) /
                    Number(supplier.number_item) /
                    unitAdjustment) *
                  1000
                ).toFixed(2),
              )
            : supplier &&
              supplier.internal_capacity &&
              supplier.internal_capacity.internal_capacity_count &&
              supplier.internal_capacity.number_item &&
              materialCost
            ? String(
                (
                  ((Number(supplier.cost_price) * (supplier.tax / 100 + 1)) /
                    (materialCost.yield_rate / 100) /
                    Number(supplier.internal_capacity.internal_capacity_count) /
                    Number(supplier.internal_capacity.number_item) /
                    unitAdjustment) *
                  1000
                ).toFixed(2),
              )
            : '',
        gramPerPiece:
          supplier && supplier.gram_per_piece
            ? String(supplier.gram_per_piece * gramPerPieceAdjustment)
            : '',
        piecePerUnitPrice:
          supplier &&
          supplier.internal_capacity_count &&
          supplier.number_item &&
          supplier.gram_per_piece &&
          materialCost
            ? String(
                (
                  ((Number(supplier.cost_price) * (supplier.tax / 100 + 1)) /
                    (materialCost.yield_rate / 100) /
                    Number(supplier.internal_capacity_count) /
                    Number(supplier.number_item) /
                    unitAdjustment) *
                  (Number(supplier.gram_per_piece) * gramPerPieceAdjustment)
                ).toFixed(2),
              )
            : '',
      }
      row.energy = material ? String(material.energy) : ''
      row.protein = material ? String(material.protein) : ''
      row.lipid = material ? String(material.lipid) : ''
      row.carbohydrate = material ? String(material.carbohydrate) : ''
      row.saltEquivalent = material ? String(material.salt_equivalent) : ''
      const allergens = material
        ? new Set([...material.base_allergens, ...material.additive_allergens])
        : new Set()
      let allergensText = new Set()
      if (allergens.has(Allergens.EGG)) {
        allergensText.add('卵')
      }
      if (allergens.has(Allergens.MILK)) {
        allergensText.add('乳')
      }
      if (allergens.has(Allergens.WHEAT)) {
        allergensText.add('小麦')
      }
      if (allergens.has(Allergens.SHRIMP)) {
        allergensText.add('えび')
      }
      if (allergens.has(Allergens.CRUB)) {
        allergensText.add('かに')
      }
      if (allergens.has(Allergens.PEANUTS)) {
        allergensText.add('落花生')
      }
      if (allergens.has(Allergens.SOBA)) {
        allergensText.add('そば')
      }
      if (allergens.has(Allergens.ALMOND)) {
        allergensText.add('アーモンド')
      }
      if (allergens.has(Allergens.ABALONE)) {
        allergensText.add('あわび')
      }
      if (allergens.has(Allergens.SQUID)) {
        allergensText.add('いか')
      }
      if (allergens.has(Allergens.IKURA)) {
        allergensText.add('いくら')
      }
      if (allergens.has(Allergens.ORANGE)) {
        allergensText.add('オレンジ')
      }
      if (allergens.has(Allergens.CASHEWNUTS)) {
        allergensText.add('カシューナッツ')
      }
      if (allergens.has(Allergens.PISTACHIO)) {
        allergensText.add('ピスタチオ')
      }
      if (allergens.has(Allergens.KIWIFRUIT)) {
        allergensText.add('キウイフルーツ')
      }
      if (allergens.has(Allergens.BEEF)) {
        allergensText.add('牛肉')
      }
      if (allergens.has(Allergens.WALNUT)) {
        allergensText.add('くるみ')
      }
      if (allergens.has(Allergens.SESAME)) {
        allergensText.add('ごま')
      }
      if (allergens.has(Allergens.SALMON)) {
        allergensText.add('鮭')
      }
      if (allergens.has(Allergens.MACKEREL)) {
        allergensText.add('鯖')
      }
      if (allergens.has(Allergens.SOY)) {
        allergensText.add('大豆')
      }
      if (allergens.has(Allergens.CHICKEN)) {
        allergensText.add('鶏肉')
      }
      if (allergens.has(Allergens.BANANA)) {
        allergensText.add('バナナ')
      }
      if (allergens.has(Allergens.PORK)) {
        allergensText.add('豚肉')
      }
      if (allergens.has(Allergens.MACADAMIANUTS)) {
        allergensText.add('マカダミアナッツ')
      }
      if (allergens.has(Allergens.PEACH)) {
        allergensText.add('桃')
      }
      if (allergens.has(Allergens.YAM)) {
        allergensText.add('山芋')
      }
      if (allergens.has(Allergens.APPLE)) {
        allergensText.add('りんご')
      }
      if (allergens.has(Allergens.GELATINE)) {
        allergensText.add('ゼラチン')
      }
      row.allergens = [...allergensText].join(',')
      row.materialType =
        material && material.single_material
          ? '単一原材料'
          : material && material.composite_material
          ? '複合原材料'
          : 'なし'
      row.materialName =
        material && material.single_material
          ? material.material_name
          : material && material.composite_material
          ? material.composite_material_name
          : ''
      const constituteMaterialNames: string[] = []
      if (material && material.constitute_material.length > 0) {
        material.constitute_material.forEach((ConstituteMaterial) => {
          constituteMaterialNames.push(ConstituteMaterial.material_name)
        })
      }
      row.rawMaterialName =
        material && material.single_material
          ? material.raw_material_name
          : material &&
            material.composite_material &&
            material.constitute_material.length > 0
          ? constituteMaterialNames.join(',')
          : ''
      row.originName =
        material && material.origin_name ? material.origin_name : ''
      const additiveNames: string[] = []
      if (material && material.additive.length > 0) {
        await Promise.all(
          material.additive.map(async (additive) => {
            let additiveUse: AdditiveUseType | null = null
            if (additive.additive_use_ref) {
              const additiveUsePayload = await dispatch(
                getAdditiveUseById({ id: additive.additive_use_ref.id }),
              )
              additiveUse = additiveUsePayload.payload
            }
            // if (additive.additive_material && additiveUse) {
            //   if (additive.disabled_material_name || additiveUse.disabled_material_name) {
            //     // 物質名は表示しない
            //     additiveNames.push(additiveUse.name)
            //   } else {
            //     additiveNames.push(additiveUse.name + "(" + additive.additive_material + ")")
            //   }
            // } else if (additive.additive_material && !additive.disabled_material_name) {
            //   additiveNames.push(additive.additive_material)
            // } else if (additiveUse && !additiveUse.disabled_material_name) {
            //   additiveNames.push(additiveUse.name)
            // }
            if (additive.additive_material && additiveUse) {
              additiveNames.push(
                additiveUse.name + '(' + additive.additive_material + ')',
              )
            } else if (additive.additive_material) {
              additiveNames.push(additive.additive_material)
            } else if (additiveUse) {
              additiveNames.push(additiveUse.name)
            }
          }),
        )
      }
      row.additiveName = additiveNames.join(',')
      if (staff?.isAdmin) {
        // スーパー管理者の場合にのみ出力する項目
        row.sourceInformation =
          material && material.source_information === 'standard'
            ? '規格書'
            : material && material.source_information === 'web'
            ? 'Web'
            : material && material.source_information === 'eight_orders'
            ? '日本食品標準成分表2020年版 八訂'
            : material && material.source_information === 'package'
            ? 'パッケージ'
            : ''
        row.url1 = material && material.urls.length > 0 ? material.urls[0] : ''
        row.url2 = material && material.urls.length > 1 ? material.urls[1] : ''
        row.url3 = material && material.urls.length > 2 ? material.urls[2] : ''
        row.classifiedName =
          material && material.classified_name ? material.classified_name : ''
        row.author = material && material.author ? material.author : ''
        row.memo = material && material.memo ? material.memo : ''
      }
      return row
    },
    [dispatch, materialCategories, materialSubCategories, staff],
  )

  const initCsvData = useCallback(async () => {
    const materialDuplicateIds: string[] = petternInfo.allMaterials
      .filter((material) => material.materialId)
      .map((material) => (material.materialId ? material.materialId : ''))
    const materialIds: string[] = Array.from(new Set(materialDuplicateIds))
    const csvRows: CsvOutputMaterialCostInfo[] = []
    await Promise.all(
      materialIds.map(async (materialId: string) => {
        const row = await buildMaterialCostRow(materialId)
        if (row) {
          csvRows.push(row)
        }
      }),
    )
    setMaterialCsvData(csvRows)
  }, [petternInfo.allMaterials, buildMaterialCostRow])

  // レシピ形式（生地・フィリングでグループ化）のダウンロード用データ
  const initRecipeCsvData = useCallback(async () => {
    const groups = collectRecipeGroups(petternInfo)
    // 同一原材料の再取得を避けるためのキャッシュ
    const rowCache = new Map<string, CsvOutputMaterialCostInfo | null>()
    const getRow = async (
      materialId: string,
    ): Promise<CsvOutputMaterialCostInfo | null> => {
      if (rowCache.has(materialId)) {
        return rowCache.get(materialId) ?? null
      }
      const row = await buildMaterialCostRow(materialId)
      rowCache.set(materialId, row)
      return row
    }
    // 先にユニークなIDを取得しておく（キャッシュを温める）
    const uniqueIds = Array.from(
      new Set(groups.flatMap((group) => group.materialIds)),
    )
    await Promise.all(uniqueIds.map((id) => getRow(id)))

    const recipeRows: RecipeCsvOutputMaterialCostInfo[] = []
    for (const group of groups) {
      for (const materialId of group.materialIds) {
        if (!materialId) {
          continue
        }
        const base = await getRow(materialId)
        if (!base) {
          continue
        }
        recipeRows.push({
          ...base,
          // ゾーン名・パターン階層は各行に表示する
          zone: group.zone,
          patternNames: group.patternPath,
        })
      }
    }
    setRecipeCsvData(recipeRows)
  }, [petternInfo, buildMaterialCostRow])

  useEffect(() => {
    initCsvData()
    initRecipeCsvData()
  }, [petternInfo.allMaterials])

  const handlerCsvDownloadButton = async (
    e:
      | MouseEvent<HTMLButtonElement, MouseEvent>
      | MouseEvent<HTMLButtonElement>
      | undefined,
    format: 'xlsx' | 'csv',
  ) => {
    if (!e) {
      return
    }
    e.preventDefault()

    const workbook = new ExcelJS.Workbook()
    workbook.addWorksheet('sheet1')
    const worksheet = workbook.getWorksheet('sheet1')

    if (!worksheet) {
      return
    }

    const columns = [
      { header: 'ID', key: 'materialId' },
      { header: '材料名', key: 'productName' },
      { header: 'メーカー', key: 'manufacturer' },
      { header: '大カテゴリー', key: 'categoryName' },
      { header: '小カテゴリー', key: 'subCategoryName' },
      { header: '内容量', key: 'internalCapacityCount' },
      { header: '内容量(単位)', key: 'internalCapacityUnit' },
      { header: '入数', key: 'numberItem' },
      { header: '入数(単位)', key: 'numberItemUnit' },
      { header: '仕入れ先カテゴリー', key: 'supplierCategory' },
      { header: '仕入先会社名', key: 'supplierCompanyName' },
      { header: '仕入先支社名', key: 'supplierBranchName' },
      { header: '仕入れ値(税抜)', key: 'costPrice' },
      { header: '仕入れ値(税込)', key: 'costPriceIncludeTax' },
      { header: '仕入れ値税率(%)', key: 'tax' },
      { header: '可食部(g)', key: 'ediblePart' },
      { header: '不可食部(g)', key: 'inEdiblePart' },
      { header: '可食部率(%)', key: 'yieldRate' },
      { header: 'g単価', key: 'gramUnitPrice' },
      { header: 'kg単価', key: 'kilogramUnitPrice' },
      { header: '1個あたりの重量(g)', key: 'gramPerPiece' },
      { header: '1個単価', key: 'piecePerUnitPrice' },
    ]
    columns.push({ header: '原材料種類', key: 'materialType' })
    columns.push({ header: '名称', key: 'materialName' })
    columns.push({ header: '構成原材料名', key: 'rawMaterialName' })
    columns.push({ header: '原料原産地名', key: 'originName' })
    columns.push({ header: '添加物', key: 'additiveName' })
    columns.push({ header: 'アレルゲン(特定原材料)', key: 'allergens' })
    columns.push({ header: 'エネルギー(kcal)', key: 'energy' })
    columns.push({ header: 'タンパク質(g)', key: 'protein' })
    columns.push({ header: '脂質(g)', key: 'lipid' })
    columns.push({ header: '炭水化物(g)', key: 'carbohydrate' })
    columns.push({ header: '食塩相当量(g)', key: 'saltEquivalent' })

    if (staff?.isAdmin) {
      // スーパー管理者の場合にのみ出力する項目
      columns.push({ header: '情報元', key: 'sourceInformation' })
      columns.push({ header: 'URL1', key: 'url1' })
      columns.push({ header: 'URL2', key: 'url2' })
      columns.push({ header: 'URL3', key: 'url3' })
      columns.push({ header: '分類名', key: 'classifiedName' })
      columns.push({ header: '登録者', key: 'author' })
      columns.push({ header: 'メモ', key: 'memo' })
    }

    if (worksheet) {
      worksheet.columns = columns
      worksheet.addRows(materialCsvData)

      const uint8Array =
        format === 'xlsx'
          ? await workbook.xlsx.writeBuffer()
          : await workbook.csv.writeBuffer()
      const bom = new Uint8Array([0xef, 0xbb, 0xbf])
      const blob = new Blob([bom, uint8Array], {
        type: 'application/octet-binary',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = '使用中原材料.' + format
      a.click()
      a.remove()
    }
  }

  // レシピ形式（生地・フィリング名でグループ化＋商品名つき）でダウンロードする
  const handlerRecipeCsvDownloadButton = async (
    e:
      | MouseEvent<HTMLButtonElement, MouseEvent>
      | MouseEvent<HTMLButtonElement>
      | undefined,
    format: 'xlsx' | 'csv',
  ) => {
    if (!e) {
      return
    }
    e.preventDefault()

    const workbook = new ExcelJS.Workbook()
    workbook.addWorksheet('sheet1')
    const worksheet = workbook.getWorksheet('sheet1')

    if (!worksheet) {
      return
    }

    // パターン階層の最大深さに合わせて「生地P・フィリングP名①②③…」列を動的に生成する。
    // 入れ子が無いレシピでも最低1列（①）は出す。
    const circledNumbers = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨']
    const patternDepthLabel = (index: number): string =>
      circledNumbers[index] ?? `(${index + 1})`
    const maxPatternDepth = Math.max(
      1,
      ...recipeCsvData.map((row) => row.patternNames.length),
    )
    const patternColumns = Array.from(
      { length: maxPatternDepth },
      (_, index) => ({
        header: `生地P・フィリングP名${patternDepthLabel(index)}`,
        key: `patternName${index + 1}`,
      }),
    )

    const columns = [
      { header: 'ID', key: 'materialId' },
      { header: '生地・フィリング', key: 'zone' },
      ...patternColumns,
      { header: '材料名', key: 'productName' },
      { header: 'メーカー', key: 'manufacturer' },
      { header: '大カテゴリー', key: 'categoryName' },
      { header: '小カテゴリー', key: 'subCategoryName' },
      { header: '内容量', key: 'internalCapacityCount' },
      { header: '内容量(単位)', key: 'internalCapacityUnit' },
      { header: '入数', key: 'numberItem' },
      { header: '入数(単位)', key: 'numberItemUnit' },
      { header: '仕入れ先カテゴリー', key: 'supplierCategory' },
      { header: '仕入先会社名', key: 'supplierCompanyName' },
      { header: '仕入先支社名', key: 'supplierBranchName' },
      { header: '仕入れ値(税抜)', key: 'costPrice' },
      { header: '仕入れ値(税込)', key: 'costPriceIncludeTax' },
      { header: '仕入れ値税率(%)', key: 'tax' },
      { header: '可食部(g)', key: 'ediblePart' },
      { header: '不可食部(g)', key: 'inEdiblePart' },
      { header: '可食部率(%)', key: 'yieldRate' },
      { header: 'g単価', key: 'gramUnitPrice' },
      { header: 'kg単価', key: 'kilogramUnitPrice' },
      { header: '1個あたりの重量(g)', key: 'gramPerPiece' },
      { header: '1個単価', key: 'piecePerUnitPrice' },
    ]
    columns.push({ header: '原材料種類', key: 'materialType' })
    columns.push({ header: '名称', key: 'materialName' })
    columns.push({ header: '構成原材料名', key: 'rawMaterialName' })
    columns.push({ header: '原料原産地名', key: 'originName' })
    columns.push({ header: '添加物', key: 'additiveName' })
    columns.push({ header: 'アレルゲン(特定原材料)', key: 'allergens' })
    columns.push({ header: 'エネルギー(kcal)', key: 'energy' })
    columns.push({ header: 'タンパク質(g)', key: 'protein' })
    columns.push({ header: '脂質(g)', key: 'lipid' })
    columns.push({ header: '炭水化物(g)', key: 'carbohydrate' })
    columns.push({ header: '食塩相当量(g)', key: 'saltEquivalent' })

    if (staff?.isAdmin) {
      // スーパー管理者の場合にのみ出力する項目
      columns.push({ header: '情報元', key: 'sourceInformation' })
      columns.push({ header: 'URL1', key: 'url1' })
      columns.push({ header: 'URL2', key: 'url2' })
      columns.push({ header: 'URL3', key: 'url3' })
      columns.push({ header: '分類名', key: 'classifiedName' })
      columns.push({ header: '登録者', key: 'author' })
      columns.push({ header: 'メモ', key: 'memo' })
    }

    if (worksheet) {
      worksheet.columns = columns
      // パターン階層を patternName1, patternName2, … の列キーに平坦化する
      const rows = recipeCsvData.map((row) => ({
        ...row,
        ...Object.fromEntries(
          Array.from({ length: maxPatternDepth }, (_, index) => [
            `patternName${index + 1}`,
            row.patternNames[index] ?? '',
          ]),
        ),
      }))
      worksheet.addRows(rows)
      // 先頭に商品名の見出し行を挿入（ヘッダー行はその下にずれる）
      // 1行目はA列を空け、B列に商品名を表示（IDが左端のため）
      worksheet.insertRow(1, [null, '商品名：' + breadName])

      const uint8Array =
        format === 'xlsx'
          ? await workbook.xlsx.writeBuffer()
          : await workbook.csv.writeBuffer()
      const bom = new Uint8Array([0xef, 0xbb, 0xbf])
      const blob = new Blob([bom, uint8Array], {
        type: 'application/octet-binary',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download =
        (breadName ? breadName + '_' : '') + '使用中原材料_レシピ形式.' + format
      a.click()
      a.remove()
    }
  }

  // レシピ未確定（下書き＝複製直後を含む）
  const isRecipeUnconfirmed = breadRecipeStatus !== BreadStatus.PUBLIC
  // 案内文に出す未登録原材料名。ライブ計算の結果を優先し、無ければ保存値を使う
  const unregisteredMaterialNameList =
    unregisteredMaterialNames.length > 0
      ? unregisteredMaterialNames
      : breadBackingStickerStatus === BackingStickerStatus.ANALYSIS
      ? sealAnalysisMaterialNames
      : []
  // 原価が出せない理由が「確定待ち」か「入力不足」かで表示を振り分ける
  const { isCostPending, isCostAlert, isCostRatePending, isCostRateAlert } =
    resolveCostDisplayState({
      costPrice: costPrice !== null ? Number(costPrice) : null,
      sellPrice: sellPrice !== null ? Number(sellPrice) : null,
      isCostCalculating: isCostCalculating,
      hasUnregisteredMaterial: hasUnregisteredMaterial,
      isRecipeUnconfirmed: isRecipeUnconfirmed,
    })

  return (
    <div className={styles.bread_detail_container} ref={componentRef}>
      <div className={styles.container} ref={componentRef}>
        <PageHedding title={breadName} to="/initload" />
        <div className={styles.bread_detail_display_unit}>
          <NumberBox
            list={[
              {
                main: true,
                label: '売価',
                num: sellPrice ? Math.floor(Number(sellPrice) * TAX) : null,
                unit: '円',
                annotation: '(税込)',
                alert: sellPrice === null,
                isCostCalculating: false,
              },
            ]}
            alert={sellPrice === null}
          />
          <div className={styles.bread_detail_space}></div>
          <NumberBox
            list={[
              {
                main: true,
                label: '原価',
                num: costPrice ? Number(costPrice) : null,
                unit: '円',
                annotation: '(税込)',
                alert: isCostAlert,
                isCostCalculating: isCostPending,
              },
              {
                main: false,
                label: '材料費',
                // 原価から逆算せず、ライブ計算した材料費(ロス込・小数)をそのまま表示する。
                // (原価-包材費 で逆算すると、原価のまるめにより材料費が不安定に変動するため)
                num:
                  !isCostPending && costPrice !== null
                    ? petternInfo.materialCost
                    : null,
                unit: '円',
                annotation: '(税込)',
                alert: false,
                isCostCalculating: isCostPending,
              },
              {
                main: false,
                label: '包材費',
                num: packagingCostPrice ? Number(packagingCostPrice) : null,
                unit: '円',
                annotation: '(税込)',
                alert: false,
                isCostCalculating: false,
              },
            ]}
            alert={isCostAlert}
          />
          <div className={styles.bread_detail_space}></div>
          <NumberBox
            list={[
              {
                main: true,
                label: '原価率',
                num:
                  sellPrice && costPrice
                    ? Math.floor(
                        (Number(costPrice) / (Number(sellPrice) * TAX)) * 100,
                      )
                    : null,
                unit: '%',
                annotation: null,
                alert: isCostRateAlert,
                isCostCalculating: isCostRatePending,
              },
            ]}
            alert={isCostRateAlert}
          />
          <div className={styles.bread_detail_space}></div>
          <NumberBox
            list={[
              {
                main: true,
                label: '重量',
                num: amountDoughBeforeBaking
                  ? Number(amountDoughBeforeBaking)
                  : null,
                unit: 'g',
                annotation: null,
                alert: amountDoughBeforeBaking === null,
                isCostCalculating: false,
              },
            ]}
            alert={amountDoughBeforeBaking === null}
          />
          <div className={styles.bread_detail_space_wide}></div>
          <ReactToPrint
            trigger={() => (
              <SubmitButton
                label="このページを印刷する"
                color="orange_outlined"
                size="large"
                icon="none"
                onClick={() => {}}
              />
            )}
            content={() => componentRef.current}
          />
        </div>
        <div className={styles.bread_detail_summary}>
          <div>
            <div className={styles.bread_detail_summary_thumbnail}>
              <img
                className={styles.bread_detail_summary_thumbnail}
                src={breadThumbnail ? breadThumbnail : defaultBread}
                alt="商品画像"
              ></img>
            </div>
            <div>
              <LossRateBox
                label="ロス率"
                value={ratioToPercent(lossRate)}
                unit="%"
              />
            </div>
            {petternInfo.recipes &&
              petternInfo.recipes.map((recipe) => (
                <>
                  <div>
                    <LossRateBox
                      label={
                        recipe.is_after_baking_percent
                          ? recipe.productName + 'の焼減率'
                          : recipe.productName + 'の焼成後重量'
                      }
                      value={
                        recipe.is_after_baking_percent && recipe.burnout_rate
                          ? ratioToPercent(recipe.burnout_rate)
                          : recipe.is_after_baking_gram &&
                            recipe.amount_dough_after_baking
                          ? recipe.amount_dough_after_baking
                          : 0
                      }
                      unit={recipe.is_after_baking_percent ? '%' : 'g'}
                    />
                  </div>
                </>
              ))}
          </div>
          <div className={styles.bread_detail_summary_info}>
            <div className={styles.bread_detail_summary_info_main}>
              <div className={styles.bread_detail_summary_info_main_head}>
                <span>栄養成分表示（100gあたり）※推定値</span>
              </div>
              <div className={styles.bread_detail_summary_info_main_body}>
                <span>
                  エネルギー:{energy}kcal / タンパク質:{protein}g / 脂質:{lipid}
                  g / 炭水化物:{carbohydrate}g / 食塩相当量:{saltEquivalent}g
                </span>
              </div>
            </div>
            <div className={styles.bread_detail_summary_info_main}>
              <div className={styles.bread_detail_summary_info_main_head2}>
                <span>アレルギー</span>
              </div>
              <div className={styles.bread_detail_summary_info_main_body2}>
                <AllergenIconBox
                  active={allergens.includes('小麦')}
                  allergen={AllergensIcon.WHEAT}
                  label="小麦"
                />
                <AllergenIconBox
                  active={allergens.includes('卵')}
                  allergen={AllergensIcon.EGG}
                  label="卵"
                />
                <AllergenIconBox
                  active={allergens.includes('乳成分')}
                  allergen={AllergensIcon.MILK}
                  label="乳成分"
                />
                <AllergenIconBox
                  active={allergens.includes('えび')}
                  allergen={AllergensIcon.SHRIMP}
                  label="えび"
                />
                <AllergenIconBox
                  active={allergens.includes('かに')}
                  allergen={AllergensIcon.CRUB}
                  label="かに"
                />
                <AllergenIconBox
                  active={allergens.includes('そば')}
                  allergen={AllergensIcon.SOBA}
                  label="そば"
                />
                <AllergenIconBox
                  active={allergens.includes('落花生')}
                  allergen={AllergensIcon.PEANUTS}
                  label="落花生"
                />
                <AllergenIconBox
                  active={allergens.includes('くるみ')}
                  allergen={AllergensIcon.WALNUT}
                  label="くるみ"
                />
                <AllergenIconBox
                  active={allergens.includes('カシューナッツ')}
                  allergen={AllergensIcon.CASHEWNUTS}
                  label="カシューナッツ"
                />
              </div>
            </div>
            <div className={styles.bread_detail_summary_info_sub}>
              <div className={styles.bread_detail_summary_info_sub_box}>
                <div className={styles.bread_detail_summary_info_sub_box_pad}>
                  <div
                    className={styles.bread_detail_summary_info_sub_box_head}
                  >
                    <span>添加物</span>
                  </div>
                  <div
                    className={styles.bread_detail_summary_info_sub_box_body}
                  >
                    <span>{additives}</span>
                  </div>
                </div>
              </div>
              <div className={styles.bread_detail_summary_info_sub_box}>
                <div className={styles.bread_detail_summary_info_sub_box_pad}>
                  <div
                    className={styles.bread_detail_summary_info_sub_box_head}
                  >
                    <span>原材料</span>
                  </div>
                  <div
                    className={styles.bread_detail_summary_info_sub_box_body}
                  >
                    <span>{materialNames}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.bread_add_recipe_display_unit_detail}>
          <div>
            <span className={styles.bread_add_recipe_display_unit_text}>
              表示単価
            </span>
          </div>
          <RadioButton
            isChecked={isDisplayUnitPricePerG}
            label="g単価"
            onChange={handleDisplayUnitPrice}
          />
          <RadioButton
            isChecked={isDisplayUnitPricePerKG}
            label="kg単価"
            onChange={handleDisplayUnitPrice}
          />
        </div>
        <div
          className={`${styles.bread_detail_table} ${styles.bread_add_recipe}`}
        >
          {petternInfo.recipes && petternInfo.recipes.length > 0 ? (
            <>
              <div className={styles.bread_detail_table_tab}>商品</div>
              <>
                {petternInfo.recipes.map((recipe) => (
                  <>
                    <div className={styles.bread_detail_table_container}>
                      <BreadDetailBoxPrimaryV2
                        petternInformation={recipe}
                        isDisplayUnitPricePerKG={isDisplayUnitPricePerKG}
                        sourceBreadId={sourceBreadId}
                      />
                    </div>
                    <div
                      className={styles.bread_detail_table_margin_bottom}
                    ></div>
                  </>
                ))}
              </>
            </>
          ) : (
            ''
          )}
          {petternInfo.doughs && petternInfo.doughs.length > 0 ? (
            <>
              <div className={styles.bread_detail_table_tab}>生地</div>
              {petternInfo.doughs.map((dough) => (
                <>
                  <div className={styles.bread_detail_table_container}>
                    <BreadDetailBoxPrimaryV2
                      petternInformation={dough}
                      isDisplayUnitPricePerKG={isDisplayUnitPricePerKG}
                      sourceBreadId={sourceBreadId}
                    />
                  </div>
                  <div
                    className={styles.bread_detail_table_margin_bottom}
                  ></div>
                </>
              ))}
            </>
          ) : (
            ''
          )}
          {petternInfo.fillings && petternInfo.fillings.length > 0 ? (
            <>
              <div className={styles.bread_detail_table_tab}>フィリング</div>
              <>
                {petternInfo.fillings.map((filling) => (
                  <>
                    <div className={styles.bread_detail_table_container}>
                      <BreadDetailBoxPrimaryV2
                        petternInformation={filling}
                        isDisplayUnitPricePerKG={isDisplayUnitPricePerKG}
                        sourceBreadId={sourceBreadId}
                      />
                    </div>
                    <div
                      className={styles.bread_detail_table_margin_bottom}
                    ></div>
                  </>
                ))}
              </>
            </>
          ) : (
            ''
          )}
        </div>
        <div className={styles.bread_detail_memo}>
          <AccordionField label="メモ" text={memo} />
          <div className={styles.bread_detail_memo_space}></div>
          <AccordionField label="工程" text={process} />
        </div>
        <div className={styles.bread_detail_foot}>
          <div className={styles.bread_detail_foot_button_small}>
            <SubmitButton
              label="編集する"
              color="orange"
              size="xsmall"
              icon="edit"
              onClick={() => navigate('/bread/edit/' + breadId)}
            />
            <SubmitButton
              label="複製する"
              color="orange"
              size="xsmall"
              icon="copy"
              onClick={() => navigate('/bread/copy/' + breadId)}
            />
          </div>
          <div className={styles.bread_detail_foot_button_middle}>
            {isCostCalculating ? (
              <SubmitButton
                label="シール印刷可否判定中"
                color="gray"
                size="xmiddle"
                icon="none"
                onClick={() => {}}
              />
            ) : breadBackingStickerStatus === BackingStickerStatus.OK &&
              breadStatus === BreadStatus.PUBLIC &&
              breadRecipeStatus === BreadStatus.PUBLIC ? (
              <SubmitButton
                label="裏貼りシールを印刷する"
                color="orange"
                size="xmiddle"
                icon="none"
                onClick={() =>
                  navigate('/food_nutrition_label?breadIds=' + breadId)
                }
              />
            ) : (
              <SubmitButton
                label="裏貼りシールを印刷する"
                color="gray"
                size="xmiddle"
                icon="none"
                onClick={() => {}}
              />
            )}
            <ReactToPrint
              trigger={() => (
                <SubmitButton
                  label="このページを印刷する"
                  color="orange_outlined"
                  size="xmiddle"
                  icon="none"
                  onClick={() => {}}
                />
              )}
              content={() => componentRef.current}
            />
          </div>
          {isRecipeUnconfirmed && (
            <div style={{ color: '#e2361d', fontSize: 12, marginTop: 8 }}>
              レシピが下書き状態のため原価は算出中です。レシピ登録画面で「登録する」を押すと確定します。
            </div>
          )}
          {!isRecipeUnconfirmed && hasUnregisteredMaterial && (
            <div style={{ color: '#e2361d', fontSize: 12, marginTop: 8 }}>
              {/* 未登録原材料名はライブ計算の結果を優先し、取れなければ保存値をフォールバックに使う。
                  参照切れや循環参照など名前を持たない要因のときは名前無しの文言にする */}
              {unregisteredMaterialNameList.length > 0
                ? '「' +
                  unregisteredMaterialNameList.join('」「') +
                  '」が原材料未登録のため算出中です。原材料登録後、レシピ編集で当該原材料を選び直して保存してください。'
                : '原材料が未登録のため原価は算出中です。原材料登録後、レシピ編集で当該原材料を選び直して保存してください。'}
            </div>
          )}
        </div>
        <div className={styles.bread_detail_foot}>
          <div className={styles.bread_detail_foot_button_small}></div>
          <div className={styles.bread_detail_foot_button_middle}>
            <SubmitButton
              label="使用中原材料のダウンロード"
              color="orange"
              size="xmiddle"
              icon="none"
              onClick={(e) => handlerCsvDownloadButton(e, 'csv')}
            />
            <SubmitButton
              label="レシピ形式でダウンロード"
              color="orange"
              size="xmiddle"
              icon="none"
              onClick={(e) => handlerRecipeCsvDownloadButton(e, 'csv')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default BreadDetailTemplate
